import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { toast } from "sonner";
import { PonesiCoinIcon, Coins } from "../../components/PonesiCoinIcon";
import { FeatureLocked } from "../../components/FeatureLocked";
import { Search, Check, X, Plus, AlertTriangle } from "lucide-react";

const TABS = [
  { k: "settings", label: "Podešavanja" },
  { k: "products", label: "Proizvodi" },
  { k: "options", label: "Dodaci" },
  { k: "analytics", label: "Analitika" },
  { k: "reconcile", label: "Provera dodele" },
  { k: "adjust", label: "Ispravi / dodeli" },
];

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-white rounded-2xl border p-4 ${className}`}>{children}</div>;
}

function Stat({ label, value, testid, tone = "" }) {
  return (
    <div className="bg-white rounded-2xl border p-3">
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">{label}</p>
      <p data-testid={testid} className={`font-display font-black text-xl mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

// ---------------- Settings ----------------
function SettingsTab({ rid }) {
  const [s, setS] = useState(null);
  const [pct, setPct] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/restaurant/${rid}/loyalty/settings`).then(({ data }) => {
      setS(data); setPct(String(data.loyalty_earn_percent ?? 0));
    }).catch(() => setS(null));
  }, [rid]);
  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/restaurant/${rid}/loyalty/settings`, patch);
      setS(data); setPct(String(data.loyalty_earn_percent ?? 0));
      toast.success("Sačuvano");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Nije sačuvano");
    } finally { setBusy(false); }
  };
  if (!s) return <p className="text-slate-400 text-sm">Učitavanje…</p>;
  const example = Math.floor((1000 * (Number(pct) || 0)) / 100);
  return (
    <div className="space-y-3 max-w-xl">
      <Card testid="loyalty-master-switch">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display font-black flex items-center gap-1.5"><PonesiCoinIcon size={18} />Ponesi Coins</p>
            <p className="text-sm text-slate-500 mt-0.5">
              {s.loyalty_enabled ? "Program je aktivan." : "Program je isključen. Postojeći Coins kupaca se čuvaju."}
            </p>
          </div>
          <button data-testid="loyalty-toggle" disabled={busy}
            onClick={() => save({ loyalty_enabled: !s.loyalty_enabled })}
            className={`relative w-14 h-8 rounded-full transition-colors ${s.loyalty_enabled ? "bg-brand-green" : "bg-slate-300"}`}>
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${s.loyalty_enabled ? "left-7" : "left-1"}`} />
          </button>
        </div>
        {!s.loyalty_enabled && (
          <p className="text-xs text-slate-400 mt-3">
            Isključivanje ne briše novčanike, stanja ni istoriju transakcija. Kada ponovo uključite, stanja ostaju.
          </p>
        )}
      </Card>

      <Card testid="loyalty-earn-card">
        <p className="font-display font-black">Koliko nagrađujete kupca?</p>
        <p className="text-sm text-slate-500 mt-0.5">
          Procenat završene porudžbine koji kupac dobija kao Ponesi Coins. Dozvoljeno 0–{s.earn_percent_max}%.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <input data-testid="loyalty-earn-input" type="number" min="0" max={s.earn_percent_max} step="0.5"
            value={pct} onChange={(e) => setPct(e.target.value)}
            className="w-28 border rounded-xl px-3 py-2 font-display font-black text-lg" />
          <span className="font-display font-black text-lg">%</span>
          <button data-testid="loyalty-earn-save" disabled={busy}
            onClick={() => save({ loyalty_earn_percent: Number(pct) })}
            className="ml-auto bg-brand-red hover:bg-brand-redHover text-white rounded-xl px-4 py-2 font-bold text-sm disabled:opacity-60">
            Sačuvaj
          </button>
        </div>
        <p data-testid="loyalty-earn-example" className="text-sm text-slate-600 mt-3 flex items-center gap-1.5">
          Za završenu porudžbinu od 1.000 RSD kupac dobija <Coins value={example} /> Ponesi Coins.
        </p>
        {Number(pct) === 0 && (
          <p className="text-xs text-brand-orange mt-2">
            0% je ispravno podešavanje: kupci ne dobijaju nove Coins, ali mogu da troše postojeće.
          </p>
        )}
      </Card>

      <Card>
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">Dva različita podešavanja</p>
        <p className="text-sm text-slate-600 mt-1">
          <b>Procenat nagrade</b> određuje koliko Coins kupac <b>dobija</b> od plaćenih porudžbina.
          <b> Cena u Coins</b> po proizvodu određuje koliko Coins kupac <b>troši</b>. Jedno se ne izvodi iz drugog.
        </p>
      </Card>
    </div>
  );
}

// ---------------- Products ----------------
function ProductRow({ rid, p, onSaved }) {
  const [enabled, setEnabled] = useState(p.coin_enabled);
  const [price, setPrice] = useState(p.coin_price ? String(p.coin_price) : "");
  const [busy, setBusy] = useState(false);
  const dirty = enabled !== p.coin_enabled || (Number(price) || null) !== (p.coin_price || null);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/restaurant/${rid}/loyalty/products/${p.id}`,
        { coin_enabled: enabled, coin_price: price === "" ? null : Number(price) });
      onSaved(data);
      toast.success("Sačuvano");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Nije sačuvano");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`coin-product-${p.id}`} className="bg-white rounded-2xl border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold truncate">{p.name}</p>
          <p className="text-xs text-slate-400">{p.category} · {RSD(p.price)}{p.available ? "" : " · nedostupan"}</p>
        </div>
        {p.coin_enabled && p.coin_price
          ? <span data-testid={`coin-status-${p.id}`} className="text-[10px] font-black uppercase rounded-full px-2 py-1 bg-amber-100 text-amber-700 flex items-center gap-1">
              <PonesiCoinIcon size={12} />{p.coin_price}
            </span>
          : <span data-testid={`coin-status-${p.id}`} className="text-[10px] font-black uppercase rounded-full px-2 py-1 bg-slate-100 text-slate-400">Bez Coins</span>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button data-testid={`coin-enable-${p.id}`} onClick={() => setEnabled((v) => !v)}
          className={`rounded-xl px-3 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1 ${enabled ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500"}`}>
          {enabled ? <Check size={13} /> : <X size={13} />}Dostupan za Coins
        </button>
        <div className="flex items-center gap-1 ml-auto">
          <PonesiCoinIcon size={16} />
          <input data-testid={`coin-price-${p.id}`} type="number" min="1" step="1" value={price}
            disabled={!enabled} placeholder="—"
            onChange={(e) => setPrice(e.target.value)}
            className="w-24 border rounded-xl px-2 py-2 font-bold tabular-nums disabled:bg-slate-50 disabled:text-slate-300" />
        </div>
        <button data-testid={`coin-save-${p.id}`} onClick={save} disabled={busy || !dirty}
          className="bg-brand-red hover:bg-brand-redHover text-white rounded-xl px-3 py-2 text-xs font-black uppercase disabled:opacity-40">
          Sačuvaj
        </button>
      </div>
      {enabled && Number(price) > 0 && (
        <p data-testid={`coin-preview-${p.id}`} className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          Kupac vidi: <b className="text-slate-700">{RSD(p.price)}</b> / <Coins value={price} />
        </p>
      )}
    </div>
  );
}

function ProductsTab({ rid }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const load = () => api.get(`/restaurant/${rid}/loyalty/products`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [rid]);
  const shown = useMemo(() => (rows || []).filter((p) => {
    if (only === "on" && !(p.coin_enabled && p.coin_price)) return false;
    if (only === "off" && p.coin_enabled && p.coin_price) return false;
    const s = `${p.name} ${p.category}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  }), [rows, q, only]);
  if (!rows) return <p className="text-slate-400 text-sm">Učitavanje…</p>;
  const on = rows.filter((p) => p.coin_enabled && p.coin_price).length;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Sa Coins cenom" value={on} testid="coin-products-on" tone="text-brand-green" />
        <Stat label="Bez Coins cene" value={rows.length - on} testid="coin-products-off" />
        <Stat label="Najniža nagrada" testid="coin-cheapest"
          value={on ? Math.min(...rows.filter((p) => p.coin_enabled && p.coin_price).map((p) => p.coin_price)) : "—"} />
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="coin-product-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Pretraži proizvode" className="w-full border rounded-xl pl-9 pr-3 py-2 text-sm" />
        </div>
        <select data-testid="coin-product-filter" value={only} onChange={(e) => setOnly(e.target.value)}
          className="border rounded-xl px-2 py-2 text-sm font-semibold">
          <option value="all">Svi</option>
          <option value="on">Sa Coins</option>
          <option value="off">Bez Coins</option>
        </select>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {shown.map((p) => (
          <ProductRow key={p.id} rid={rid} p={p}
            onSaved={(d) => setRows((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x)))} />
        ))}
        {shown.length === 0 && <p className="text-slate-400 text-sm">Nema rezultata.</p>}
      </div>
    </div>
  );
}

// ---------------- Paid options ----------------
function OptionsTab({ rid }) {
  const [groups, setGroups] = useState(null);
  useEffect(() => {
    api.get(`/restaurant/${rid}/loyalty/options`).then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  }, [rid]);
  const save = async (gid, item, value) => {
    try {
      const { data } = await api.put(`/restaurant/${rid}/loyalty/options/${gid}/items/${item.id}`,
        { coin_price: value === "" ? null : Number(value) });
      setGroups((prev) => prev.map((g) => (g.id !== gid ? g : {
        ...g,
        items: g.items.map((i) => (i.id === item.id ? { ...i, coin_price: data.coin_price } : i)),
        missing_coin_price: g.items.filter((i) => i.paid && !(i.id === item.id ? data.coin_price : i.coin_price)).length,
      })));
      toast.success("Sačuvano");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Nije sačuvano");
    }
  };
  if (!groups) return <p className="text-slate-400 text-sm">Učitavanje…</p>;
  const missing = groups.reduce((a, g) => a + g.missing_coin_price, 0);
  return (
    <div className="space-y-3">
      {missing > 0 && (
        <div data-testid="coin-options-warning" className="rounded-2xl border border-brand-orange/40 bg-orange-50 p-3 flex gap-2">
          <AlertTriangle size={18} className="text-brand-orange shrink-0" />
          <p className="text-sm text-slate-700">
            {missing} plaćenih dodataka nema cenu u Coins. Korpa koja sadrži takav dodatak ne može da se plati Coinsima.
          </p>
        </div>
      )}
      {groups.map((g) => (
        <Card key={g.id} testid={`coin-group-${g.id}`}>
          <div className="flex items-center justify-between">
            <p className="font-display font-black">{g.name}</p>
            <span className="text-xs text-slate-400">{g.paid_items} plaćenih</span>
          </div>
          <div className="mt-2 divide-y">
            {g.items.map((i) => (
              <div key={i.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{i.name}</p>
                  <p className="text-xs text-slate-400">{i.paid ? RSD(i.price) : "Besplatan dodatak · 0 Coins"}</p>
                </div>
                {i.paid ? (
                  <div className="flex items-center gap-1">
                    <PonesiCoinIcon size={15} />
                    <input data-testid={`coin-option-${i.id}`} type="number" min="1" step="1"
                      defaultValue={i.coin_price || ""} placeholder="—"
                      onBlur={(e) => { if (String(e.target.value) !== String(i.coin_price || "")) save(g.id, i, e.target.value); }}
                      className={`w-24 border rounded-xl px-2 py-1.5 text-sm font-bold tabular-nums ${!i.coin_price ? "border-brand-orange/60 bg-orange-50" : ""}`} />
                  </div>
                ) : <span className="text-xs font-bold text-slate-300">0</span>}
              </div>
            ))}
          </div>
        </Card>
      ))}
      {groups.length === 0 && <p className="text-slate-400 text-sm">Nema grupa opcija.</p>}
    </div>
  );
}

// ---------------- Analytics ----------------
function AnalyticsTab({ rid }) {
  const [d, setD] = useState(null);
  const [preset, setPreset] = useState("last_28");
  useEffect(() => {
    setD(null);
    api.get(`/restaurant/${rid}/loyalty/analytics?preset=${preset}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [rid, preset]);
  return (
    <div className="space-y-3">
      <select data-testid="coin-analytics-range" value={preset} onChange={(e) => setPreset(e.target.value)}
        className="border rounded-xl px-3 py-2 text-sm font-semibold">
        <option value="last_7">Poslednjih 7 dana</option>
        <option value="last_28">Poslednjih 28 dana</option>
        <option value="last_90">Poslednjih 90 dana</option>
      </select>
      {!d ? <p className="text-slate-400 text-sm">Učitavanje…</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Izdati Coins" value={d.coins_issued} testid="coin-issued" tone="text-brand-green" />
            <Stat label="Potrošeni Coins" value={d.coins_redeemed} testid="coin-redeemed" />
            <Stat label="Aktivni novčanici" value={d.active_wallets} testid="coin-wallets" />
            <Stat label="Kupci sa stanjem" value={d.customers_with_balance} testid="coin-with-balance" />
            <Stat label="Porudžbine Coinsima" value={d.coin_paid_orders} testid="coin-orders" />
            <Stat label="Coins u obavezi" value={d.outstanding_coins} testid="coin-outstanding" />
            <Stat label="Ručno dodeljeno" value={d.coins_adjusted} testid="coin-adjusted" />
            <Stat label="Vraćeno (otkazi)" value={d.coins_refunded} testid="coin-refunded" />
          </div>
          <Card testid="coin-reference-value">
            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
              Referentna vrednost nagrada (nije prihod)
            </p>
            <p data-testid="coin-reference-amount" className="font-display font-black text-2xl mt-0.5">{RSD(d.reference_reward_value_rsd)}</p>
            <p className="text-xs text-slate-400 mt-1">
              Maloprodajna vrednost proizvoda datih za Coins. Ne ulazi u gotovinu, karticu ni u prihod.
            </p>
          </Card>
          <Stat label="Kupci koji su više puta platili Coinsima" value={d.repeat_coin_customers} testid="coin-repeat" />
        </>
      )}
    </div>
  );
}

// ---------------- Manual adjustment ----------------
function AdjustTab({ rid }) {
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState([]);
  const loadLedger = () => api.get(`/restaurant/${rid}/loyalty/ledger?limit=20`).then(({ data }) => setLedger(data)).catch(() => setLedger([]));
  useEffect(() => { loadLedger(); }, [rid]);
  const lookup = async () => {
    setFound(null);
    try {
      const { data } = await api.get(`/restaurant/${rid}/loyalty/lookup?order_code=${encodeURIComponent(code.trim())}`);
      setFound(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Porudžbina nije pronađena");
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/restaurant/${rid}/loyalty/adjustments`,
        { order_code: found.public_code, amount: Number(amount), reason });
      toast.success("Coins su dodeljeni");
      setAmount(""); setReason(""); setFound(null); setCode("");
      loadLedger();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Nije uspelo");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 max-w-xl">
      <Card testid="coin-adjust-card">
        <p className="font-display font-black">Ispravi / dodeli Coins</p>
        <p className="text-sm text-slate-500 mt-0.5">
          Unesite šifru porudžbine iz vašeg restorana. Kupac se prepoznaje iz porudžbine.
        </p>
        <div className="flex gap-2 mt-3">
          <input data-testid="coin-adjust-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="npr. AB7" className="flex-1 border rounded-xl px-3 py-2 font-display font-black uppercase" />
          <button data-testid="coin-adjust-lookup" onClick={lookup} disabled={!code.trim()}
            className="bg-slate-900 text-white rounded-xl px-4 py-2 font-bold text-sm disabled:opacity-40">Pronađi</button>
        </div>
        {found && (
          <div data-testid="coin-adjust-found" className="mt-3 rounded-xl bg-slate-50 border p-3 text-sm space-y-1">
            <p>Porudžbina: <b>{found.public_code}</b></p>
            <p>Kupac: <b>{found.customer_label}</b></p>
            <p className="flex items-center gap-1">Trenutno stanje: <Coins value={found.balance} testid="coin-adjust-balance" /> Coins</p>
            <div className="grid gap-2 pt-2">
              <div className="flex items-center gap-2">
                <PonesiCoinIcon size={16} />
                <input data-testid="coin-adjust-amount" type="number" min="1" step="1" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="Broj Coins"
                  className="flex-1 border rounded-xl px-3 py-2 font-bold" />
              </div>
              <input data-testid="coin-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Razlog (obavezno)" className="border rounded-xl px-3 py-2 text-sm" />
              <div className="flex flex-wrap gap-1">
                {[`Nedostajući Coins za porudžbinu ${found.public_code}`, "Goodwill — reklamacija kupca"].map((r) => (
                  <button key={r} data-testid={`coin-reason-${r.slice(0, 8)}`} onClick={() => setReason(r)}
                    className="text-[11px] font-semibold bg-slate-100 rounded-full px-2.5 py-1 text-slate-600">{r}</button>
                ))}
              </div>
              <button data-testid="coin-adjust-submit" onClick={submit}
                disabled={busy || !(Number(amount) > 0) || !reason.trim()}
                className="bg-brand-green text-white rounded-xl py-2.5 font-black uppercase text-sm flex items-center justify-center gap-1 disabled:opacity-40">
                <Plus size={15} />Dodeli Coins
              </button>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          Moguće je samo dodavanje Coins. Oduzimanje je rezervisano za Ponesi administraciju.
          Svaka promena se trajno upisuje u istoriju transakcija.
        </p>
      </Card>

      <Card testid="coin-ledger">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">Zadnje transakcije</p>
        <div className="divide-y">
          {ledger.map((t) => (
            <div key={t.id} data-testid={`coin-tx-${t.id}`} className="py-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold">{t.type}</p>
                <p className="text-xs text-slate-400 truncate">
                  {t.metadata?.public_code || t.metadata?.order_number || "—"}
                  {t.metadata?.reason ? ` · ${t.metadata.reason}` : ""}
                </p>
              </div>
              <span className={`font-display font-black tabular-nums ${t.amount > 0 ? "text-brand-green" : "text-brand-red"}`}>
                {t.amount > 0 ? "+" : ""}{t.amount}
              </span>
            </div>
          ))}
          {ledger.length === 0 && <p className="text-slate-400 text-sm py-2">Još nema transakcija.</p>}
        </div>
      </Card>
    </div>
  );
}

// Detect-only check for completed orders that never generated an EARN transaction, plus an
// explicit per-order correction through the canonical loyalty service (never a mass backfill).
function ReconcileTab({ rid }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setD(null); setSel([]);
    api.get(`/restaurant/${rid}/loyalty/reconcile?preset=last_28`).then(({ data }) => setD(data)).catch(() => setD(null));
  };
  useEffect(() => { load(); }, [rid]);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurant/${rid}/loyalty/reconcile`, { order_ids: sel });
      toast.success(`Dodeljeno ${data.coins_awarded} Coins za ${data.processed} porudžbina`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Nije uspelo");
    } finally { setBusy(false); }
  };
  if (!d) return <p className="text-slate-400 text-sm">Učitavanje…</p>;
  return (
    <div className="space-y-3 max-w-2xl" data-testid="coin-reconcile">
      <Card>
        <p className="font-display font-black">Provera dodele Coins</p>
        <p className="text-sm text-slate-500 mt-0.5">
          Završene porudžbine iz poslednjih 28 dana koje su ispunile uslove, a nisu dobile Coins.
          Provera ništa ne dodeljuje sama — vi birate porudžbine.
        </p>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <Stat label="Provereno" value={d.checked_orders} testid="recon-checked" />
          <Stat label="Bez Coins" value={d.missing_earn_count} testid="recon-missing"
            tone={d.missing_earn_count ? "text-brand-orange" : ""} />
          <Stat label="Coins ukupno" value={d.missing_earn_coins} testid="recon-coins" />
        </div>
        {!d.loyalty_enabled && (
          <p className="text-xs text-brand-orange mt-2">Program je isključen — uključite ga pre dodele.</p>
        )}
      </Card>
      {d.items.length > 0 && (
        <Card testid="recon-list">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">Porudžbine</p>
            <button data-testid="recon-select-all"
              onClick={() => setSel(sel.length === d.items.length ? [] : d.items.map((x) => x.order_id))}
              className="text-xs font-bold text-brand-red">
              {sel.length === d.items.length ? "Poništi sve" : "Izaberi sve"}
            </button>
          </div>
          <div className="divide-y">
            {d.items.map((x) => (
              <label key={x.order_id} data-testid={`recon-item-${x.order_number}`}
                className="flex items-center gap-2 py-2 cursor-pointer">
                <input type="checkbox" data-testid={`recon-check-${x.order_number}`}
                  checked={sel.includes(x.order_id)}
                  onChange={() => setSel((p) => p.includes(x.order_id) ? p.filter((i) => i !== x.order_id) : [...p, x.order_id])}
                  className="w-4 h-4" />
                <span className="font-display font-black">{x.public_code || x.order_number}</span>
                <span className="text-xs text-slate-400">{RSD(x.final_eligible_total_rsd)}</span>
                <Coins value={x.expected_coins} className="ml-auto text-brand-green" />
              </label>
            ))}
          </div>
          <button data-testid="recon-apply" onClick={apply} disabled={busy || !sel.length}
            className="w-full mt-3 bg-brand-green text-white rounded-xl py-2.5 font-black uppercase text-sm disabled:opacity-40">
            Dodeli Coins za izabrane ({sel.length})
          </button>
        </Card>
      )}
      {d.items.length === 0 && (
        <p data-testid="recon-empty" className="text-sm text-brand-green font-semibold">
          Sve završene porudžbine su ispravno nagrađene.
        </p>
      )}
    </div>
  );
}

export default function LoyaltyPage() {
  const { restaurant, features } = useOutletContext();
  const [tab, setTab] = useState("settings");
  const rid = restaurant.id;
  if (features?.PONESI_COINS === false) {
    return (
      <div data-testid="loyalty-page">
        <h1 className="font-display font-black text-2xl md:text-3xl flex items-center gap-2 mb-4">
          <PonesiCoinIcon size={26} />Ponesi Coins
        </h1>
        <FeatureLocked feature="PONESI_COINS" title="Ponesi Coins">
          <p className="text-xs text-slate-400 mt-3">
            Postojeći Coins kupaca, istorija transakcija i cene u Coins se čuvaju.
          </p>
        </FeatureLocked>
      </div>
    );
  }
  return (
    <div data-testid="loyalty-page">
      <h1 className="font-display font-black text-2xl md:text-3xl flex items-center gap-2">
        <PonesiCoinIcon size={26} />Ponesi Coins
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Program lojalnosti vašeg restorana. Coins se zarađuju i troše samo kod vas.
      </p>
      <div className="flex gap-1.5 overflow-x-auto mt-4 mb-4 pb-1">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`loyalty-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-black uppercase tracking-wider transition-colors ${tab === t.k ? "bg-slate-900 text-white" : "bg-white border text-slate-500"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "settings" && <SettingsTab rid={rid} />}
      {tab === "products" && <ProductsTab rid={rid} />}
      {tab === "options" && <OptionsTab rid={rid} />}
      {tab === "analytics" && <AnalyticsTab rid={rid} />}
      {tab === "reconcile" && <ReconcileTab rid={rid} />}
      {tab === "adjust" && <AdjustTab rid={rid} />}
    </div>
  );
}
